// File: src/pages/WareHouseList.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Row,
  Col,
  Card,
  CardBody,
  Input,
  Button,
  Spinner,
  Modal,
  ModalHeader,
  ModalBody,
  Alert,
  Form,
  FormGroup,
  Label,
} from "reactstrap";

import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import swal from "sweetalert";

import { FaPlus, FaFilter, FaPencilAlt, FaStoreAlt } from "react-icons/fa";
import { ImDownload } from "react-icons/im";
import { Trash2 } from "react-feather";

// ---- API layer you already use elsewhere ----
import { _Get, _Delete, _Put, _Put2 } from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import { exportDataToPDF } from "../import/Downloader";
import {
  Create_Warehouse_List,
  Delete_WareHouse,
  View_Wareahouse_Byid,
  Update_Wareahouse_Byid,
} from "../import/ApiEndPoint/Api";
import { CreateWarehousesave } from "../import/ApiEndPoint/ApiCalling";

// ---- tiny CSS injected ONCE (page scoped) ----
const injectOnce = () => {
  if (document.getElementById("warehouses-inline-css")) return;
  const style = document.createElement("style");
  style.id = "warehouses-inline-css";
  style.innerHTML = `
    .wh-toolbar .count-pill{
      background:#eef2ff; color:#3730a3; font-weight:700; padding:6px 10px; border-radius:999px;
    }
    .wh-table-wrap{
      width:100%; overflow:auto; border:1px solid #e5e7eb; border-radius:14px; background:#fff;
    }
    .wh-table{ width:100%; border-collapse:collapse; table-layout:auto; min-width:0; }
    .wh-table thead th{
      background:#f4f7f6; color:#0f172a; font-weight:700; text-transform:uppercase; font-size:12px; letter-spacing:.03em;
      border-bottom:1px solid #e5e7eb; padding:6px 8px; position:sticky; top:0; z-index:1; text-align:center; white-space:nowrap;
    }
    .wh-table tbody td{
      padding:6px 8px; border-bottom:1px solid #f1f5f9; vertical-align:middle; color:#0f172a; font-size:14px; text-align:center;
    }
    .wh-table tbody tr:hover{ background:#f9fbff; }

    .action-chips{ display:flex; justify-content:center; align-items:center; gap:8px }
    .chip{ display:inline-grid; place-items:center; width:28px; height:28px; border-radius:16px; }
    .chip-store{ background:#39cccc; }
    .chip-edit{ background:#d46f10; }
    .chip-delete{ background:#ec1809; }

    /* inline create/edit form */
    .wh-form-card{
      border:1px solid #e5e7eb; border-radius:14px; background:#fff; margin-top:10px;
      box-shadow: 0 6px 16px rgba(0,0,0,.06);
    }
    .wh-form-title{
      font-weight:800; font-style:bold; text-transform:uppercase; font-size:30px; letter-spacing:.02em;
    }
    .wh-form .form-group { margin-bottom:8px; }
    .wh-form label { font-size:12px; margin-bottom:4px; }
    .wh-form input, .wh-form select { font-size:12px; height:34px; }
    .wh-form .actions{ display:flex; gap:8px; justify-content:flex-end; }
  `;
  document.head.appendChild(style);
};

// ---- helpers ----
const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const formatDate = (s) => (s ? String(s).split("T")[0] : "");

export default function WareHouseList() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [downloadOpen, setDownloadOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [permissions, setPermissions] = useState({
    View: true,
    Create: true,
    Edit: true,
    Delete: true,
    Download: true,
  });

  const [rows, setRows] = useState([]);
  const [allRows, setAllRows] = useState([]);

  const [search, setSearch] = useState("");

  // Column chooser state
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const addBufferRef = useRef([]);

  // Inline form state
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState("Create"); // "Create" | "Update"
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({
    Username: "",
    Password: "",
    warehouseName: "",
    warehouseAddress: "",
    warehouseMobile: "",
    landlineNumber: "",
    status: "Active",
    id: "", // derived from warehouseName
  });
  const [numError, setNumError] = useState("");

  useEffect(() => injectOnce(), []);

  // ----- Define columns -----
  const baseColumns = useMemo(
    () => [
      {
        key: "status",
        header: "STATUS",
        get: (r) => r?.status,
        render: (r) => (
          <span
            style={{
              fontWeight: 700,
              color: r?.status === "Active" ? "#065f46" : "#7c2d12",
            }}
          >
            {r?.status || "-"}
          </span>
        ),
      },
      { key: "id", header: "WAREHOUSE ID", get: (r) => r?.id },
      {
        key: "warehouseName",
        header: "NAME",
        get: (r) => r?.warehouseName,
      },
      { key: "mobileNo", header: "MOBILE", get: (r) => r?.mobileNo },
      {
        key: "landlineNumber",
        header: "LANDLINE",
        get: (r) => r?.landlineNumber,
      },
      { key: "address", header: "ADDRESS", get: (r) => r?.address },
      {
        key: "createdAt",
        header: "CREATED",
        get: (r) => formatDate(r?.createdAt),
      },
      {
        key: "updatedAt",
        header: "UPDATED",
        get: (r) => formatDate(r?.updatedAt),
      },
    ],
    [],
  );

  const actionsColumn = useMemo(
    () => ({
      key: "__actions__",
      header: "ACTIONS",
      get: () => "",
      render: (r) => (
        <div className="action-chips">
          {permissions?.View && (
            <span className="chip chip-store" title="Open">
              <FaStoreAlt
                size={14}
                color="white"
                onClick={() =>
                  navigate(`/app/warehouses/viewonewarehouse/${r?._id}`)
                }
              />
            </span>
          )}
          {permissions?.Edit && (
            <span
              className="chip chip-edit"
              title="Edit"
              onClick={() => openInlineForm("Update", r?._id)}
            >
              <FaPencilAlt size={14} color="white" />
            </span>
          )}
          {permissions?.Delete && (
            <span
              className="chip chip-delete"
              title="Delete"
              onClick={() => handleDelete(r?._id)}
            >
              <Trash2 size={14} color="white" />
            </span>
          )}
        </div>
      ),
    }),
    [permissions, navigate],
  );

  // Column map
  const colMap = useMemo(() => {
    const map = new Map();
    baseColumns.forEach((c) => map.set(c.key, c));
    map.set(actionsColumn.key, actionsColumn);
    return map;
  }, [baseColumns, actionsColumn]);

  // Restore or default columns
  const [selectedKeys, setSelectedKeys] = useState([]);
  useEffect(() => {
    const raw = localStorage.getItem("WareHouseListCols");
    let nextKeys = [];
    try {
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed) && parsed.length) {
        nextKeys = parsed.filter((k) => colMap.has(k));
      }
    } catch {}
    if (!nextKeys.length) nextKeys = baseColumns.map((c) => c.key);
    setSelectedKeys(nextKeys);
  }, [baseColumns, colMap]);

  // ----- Fetch data -----
  const reload = async () => {
    const userinfo = JSON.parse(localStorage.getItem("userData") || "{}");
    const uid = userinfo?._id || userinfo?.id || "";
    const db = userinfo?.database || userinfo?.db || "";
    if (!uid || !db) throw new Error("Missing _id or database in userData");

    // permissions
    const inside = CheckPermission("WarehouseList");
    if (inside && typeof inside === "object") {
      setPermissions((prev) => ({ ...prev, ...inside }));
    }

    // fetch
    const res = await _Get(Create_Warehouse_List, db);
    const list = Array.isArray(res?.Warehouse) ? res.Warehouse : [];

    // If Master/SuperAdmin → all; else only own
    let filtered = list;
    const roleName = userinfo?.rolename?.roleName;
    if (!(roleName === "MASTER" || roleName === "SuperAdmin")) {
      filtered = list.filter((w) => String(w?.created_by?._id) === String(uid));
    }

    setRows(filtered);
    setAllRows(filtered);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      setErrorMsg("");
      try {
        await reload();
      } catch (e) {
        console.error("[WareHouseList] fetch error:", e);
        setErrorMsg(
          e?.response?.data?.message ||
            e?.message ||
            "Unable to load warehouses. Check console for details.",
        );
        setRows([]);
        setAllRows([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // ----- Visible columns & search -----
  const visibleCols = useMemo(() => {
    const cols = selectedKeys.map((k) => colMap.get(k)).filter(Boolean);
    return [...cols, actionsColumn]; // include Actions at end
  }, [selectedKeys, colMap, actionsColumn]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    const searchCols = visibleCols.filter((c) => c.key !== "__actions__");
    return rows.filter((r) =>
      searchCols.some((c) => toStr(c.get(r)).toLowerCase().includes(term)),
    );
  }, [rows, search, visibleCols]);

  // ----- Actions -----
  const handleDelete = (id) => {
    swal("Warning", "Sure you want to delete it?", {
      buttons: { cancel: "Cancel", catch: { text: "Delete", value: "delete" } },
    }).then((value) => {
      if (value === "delete") {
        _Delete(Delete_WareHouse, id)
          .then(() => {
            setRows((prev) => prev.filter((x) => x._id !== id));
            setAllRows((prev) => prev.filter((x) => x._id !== id));
          })
          .catch(() => {});
      }
    });
  };

  const handleExportPDF = async () => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const header = cols.map((c) => c.header).join(",");
    const lines = filteredRows.map((r) =>
      cols
        .map((c) => {
          const v = toStr(c.get(r)).replace(/"/g, '""');
          return `"${v}"`;
        })
        .join(","),
    );
    const csv = [header, ...lines].join("\r\n");
    await exportDataToPDF(csv, "WareHouseList");
  };

  const handleExportXLSX = async (fileName) => {
    const cols = visibleCols.filter((c) => c.key !== "__actions__");
    const data = filteredRows.map((r) => {
      const obj = {};
      cols.forEach((c) => (obj[c.header] = toStr(c.get(r))));
      return obj;
    });
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Sheet1");
    const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    saveAs(
      new Blob([buf], { type: "application/octet-stream" }),
      `${fileName}.xlsx`,
    );
  };

  // ----- Column Chooser Modal logic -----
  const openModal = () => {
    addBufferRef.current = [];
    setSelectedIndex(-1);
    setModal(true);
  };
  const closeModal = () => setModal(false);

  const handleCheckbox = (checked, colKey) => {
    const buf = addBufferRef.current.slice();
    if (checked) {
      if (!buf.includes(colKey)) buf.push(colKey);
    } else {
      const i = buf.indexOf(colKey);
      if (i >= 0) buf.splice(i, 1);
    }
    addBufferRef.current = buf;
  };

  const addSelectedColumns = () => {
    const next = Array.from(
      new Set([...selectedKeys, ...addBufferRef.current]),
    );
    setSelectedKeys(next);
  };

  const removeAtIndex = () => {
    if (selectedIndex < 0) return;
    const next = selectedKeys.slice();
    next.splice(selectedIndex, 1);
    setSelectedKeys(next);
    setSelectedIndex(-1);
  };

  const shiftUp = () => {
    if (selectedIndex <= 0) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex - 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex - 1);
  };

  const shiftDown = () => {
    if (selectedIndex < 0 || selectedIndex >= selectedKeys.length - 1) return;
    const next = selectedKeys.slice();
    const el = next.splice(selectedIndex, 1)[0];
    next.splice(selectedIndex + 1, 0, el);
    setSelectedKeys(next);
    setSelectedIndex(selectedIndex + 1);
  };

  const applyColumns = (e) => {
    e.preventDefault();
    localStorage.setItem("WareHouseListCols", JSON.stringify(selectedKeys));
    closeModal();
  };

  // ----- Inline Create/Edit form -----
  const resetForm = () =>
    setFormData({
      Username: "",
      Password: "",
      warehouseName: "",
      warehouseAddress: "",
      warehouseMobile: "",
      landlineNumber: "",
      status: "Active",
      id: "",
    });

  const openInlineForm = async (mode, id = null) => {
    setFormMode(mode);
    setNumError("");
    if (mode === "Create") {
      setEditId(null);
      resetForm();
      setShowForm(true);
      return;
    }
    // Update mode
    try {
      setSaving(true);
      const res = await _Get(View_Wareahouse_Byid, id);
      const w = res?.Warehouse || {};
      setEditId(id);
      setFormData({
        Username: w?.Username || "",
        Password: w?.Password || "",
        warehouseName: w?.warehouseName || "",
        warehouseAddress: w?.address || "",
        warehouseMobile: w?.mobileNo || "",
        landlineNumber: w?.landlineNumber || "",
        status: w?.status || "Active",
        id: w?.id || "",
      });
      setShowForm(true);
    } catch (e) {
      swal("Failed to load warehouse");
    } finally {
      setSaving(false);
    }
  };

  const handleFormInput = (e) => {
    const { name, value, type } = e.target;
    if (type === "number") {
      // up to 10 digits
      if (/^\d{0,10}$/.test(value)) {
        setFormData((p) => ({ ...p, [name]: value }));
        setNumError("");
      } else {
        setNumError("Please enter a valid number (max 10 digits)");
      }
      return;
    }
    if (name === "warehouseName") {
      const filteredId = value.replace(/\s/g, "");
      setFormData((p) => ({ ...p, warehouseName: value, id: filteredId }));
      return;
    }
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const submitWarehouse = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      const payload = {
        Username: formData?.Username,
        Password: formData?.Password,
        warehouseName: formData?.warehouseName,
        id: formData?.id,
        mobileNo: formData?.warehouseMobile,
        address: formData?.warehouseAddress,
        landlineNumber: formData?.landlineNumber,
        database: user?.database,
        status: formData?.status,
      };
      if (formMode === "Create") {
        const res = await CreateWarehousesave(payload);
        swal(`Warehouse ${res?.message || "saved"}`);
        await reload();
        // keep form open but clear for faster multiple adds
        resetForm();
      } else {
        await _Put2(Update_Wareahouse_Byid, editId, payload);
        swal("Updated successfully");
        await reload();
        setShowForm(false);
        closeModal();
      }
    } catch (err) {
      console.log(err);
      swal("Something went wrong");
    } finally {
      setSaving(false);
    }
  };

  // ----- Loading -----
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          marginTop: "20rem",
        }}
      >
        <Spinner style={{ height: "4rem", width: "4rem" }} color="primary">
          Loading...
        </Spinner>
      </div>
    );
  }

  const noData = !filteredRows?.length;

  return (
    <>
      {/* Error banner if fetch failed */}
      {errorMsg && (
        <Row>
          <Col sm="12">
            <Alert color="danger" style={{ marginBottom: 10 }}>
              {errorMsg}
            </Alert>
          </Col>
        </Row>
      )}

      {/* === Toolbar === */}
      <Row className="wh-toolbar" style={{ marginBottom: 10 }}>
        <Col sm="12">
          <Card
            style={{
              paddingTop: 4,
              paddingBottom: 4,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              background: "#f4f7f6",
            }}
          >
            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  fontWeight: 800,
                  textTransform: "uppercase",
                  fontSize: 18,
                }}
              >
                Warehouse List{" "}
                <span className="count-pill" title="records">
                  {filteredRows.length}
                </span>
              </div>

              <div style={{ flex: "1 1 240px" }} />

              <div
                style={{
                  minWidth: 260,
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <Input
                  placeholder="Search warehouse..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ background: "#fff" }}
                />
              </div>

              {permissions?.Create && (
                <Button
                  style={{
                    backgroundColor: "rgb(8, 91, 245)",
                    color: "white",
                    fontWeight: 600,
                    height: 32,
                    fontSize: 14,
                    textTransform: "uppercase",
                  }}
                  onClick={() => openInlineForm("Create")}
                >
                  <FaPlus size={10} /> Warehouse
                </Button>
              )}

              <Button
                outline
                color="primary"
                onClick={openModal}
                title="Filter Columns"
                style={{
                  height: 32,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FaFilter size={14} />
              </Button>

              {permissions?.Download && (
                <div
                  onMouseEnter={() => setDownloadOpen(true)}
                  onMouseLeave={() => setDownloadOpen(false)}
                  style={{ position: "relative" }}
                >
                  <ImDownload
                    style={{ cursor: "pointer" }}
                    title="download file"
                    size={24}
                    color="rgb(8, 91, 245)"
                  />
                  {downloadOpen && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: 32,
                        zIndex: 5,
                        border: "1px solid rgb(8, 91, 245)",
                        backgroundColor: "white",
                        borderRadius: 10,
                        overflow: "hidden",
                        minWidth: 130,
                        boxShadow: "0 12px 24px rgba(0,0,0,.12)",
                      }}
                    >
                      <div onClick={handleExportPDF} style={dlItemStyle}>
                        .PDF
                      </div>
                      <div
                        onClick={() => handleExportXLSX("WareHouseList")}
                        style={dlItemStyle}
                      >
                        .XLSX
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </Col>
      </Row>

      {/* === Inline Create/Update Form Card === */}
      {showForm && (
        <Row>
          <Col sm="12">
            <Card className="wh-form-card">
              <CardBody>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    marginBottom: 6,
                  }}
                >
                  <div
                    className="wh-form-title"
                    style={{ fontWeight: 800, fontSize: "18px" }}
                  >
                    {formMode} Warehouse
                  </div>
                  <div className="actions">
                    <Button
                      color="secondary"
                      size="sm"
                      onClick={() => {
                        setShowForm(false);
                        setEditId(null);
                        resetForm();
                      }}
                    >
                      Close
                    </Button>
                  </div>
                </div>

                <Form className="wh-form" onSubmit={submitWarehouse}>
                  <Row>
                    <Col lg="3" md="4" sm="12">
                      <FormGroup>
                        <Label>
                          Username <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          required
                          name="Username"
                          value={formData.Username}
                          onChange={handleFormInput}
                          placeholder="Enter username"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="3" md="4" sm="12">
                      <FormGroup>
                        <Label>
                          Password <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          required
                          type="password"
                          name="Password"
                          value={formData.Password}
                          onChange={handleFormInput}
                          placeholder="Enter password"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="3" md="4" sm="12">
                      <FormGroup>
                        <Label>
                          Warehouse Name <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          required
                          name="warehouseName"
                          value={formData.warehouseName}
                          onChange={handleFormInput}
                          placeholder="Enter name"
                        />
                      </FormGroup>
                    </Col>
                    {/* <Col lg="3" md="4" sm="12" style={{ visibility: "hidden" }}>
                      <FormGroup>
                        <Label>Warehouse ID</Label>
                        <Input readOnly value={formData.id} />
                      </FormGroup>
                    </Col> */}
                    <Col lg="3" md="4" sm="12">
                      <FormGroup>
                        <Label>
                          Mobile <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          required
                          type="number"
                          name="warehouseMobile"
                          value={formData.warehouseMobile}
                          onChange={handleFormInput}
                          placeholder="10-digit mobile"
                        />
                        {numError && (
                          <small style={{ color: "red" }}>{numError}</small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col lg="3" md="4" sm="12">
                      <FormGroup>
                        <Label>
                          Landline <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          required
                          type="number"
                          name="landlineNumber"
                          value={formData.landlineNumber}
                          onChange={handleFormInput}
                          placeholder="Landline"
                        />
                      </FormGroup>
                    </Col>
                    <Col lg="6" md="8" sm="12">
                      <FormGroup>
                        <Label>
                          Address <span style={{ color: "red" }}>*</span>
                        </Label>
                        <Input
                          required
                          name="warehouseAddress"
                          value={formData.warehouseAddress}
                          onChange={handleFormInput}
                          placeholder="Enter address"
                        />
                      </FormGroup>
                    </Col>

                    <Col lg="6" md="8" sm="12">
                      <Label className="mb-0">Status</Label>
                      <div className="d-flex align-items-center gap-3">
                        <label className="m-0 d-flex align-items-center gap-1">
                          <Input
                            type="radio"
                            name="status"
                            checked={formData.status === "Active"}
                            onChange={() =>
                              setFormData((p) => ({ ...p, status: "Active" }))
                            }
                          />
                          <span>Active</span>
                        </label>
                        <label className="m-0 d-flex align-items-center gap-1">
                          <Input
                            type="radio"
                            name="status"
                            checked={formData.status === "Deactive"}
                            onChange={() =>
                              setFormData((p) => ({ ...p, status: "Deactive" }))
                            }
                          />
                          <span>Deactive</span>
                        </label>
                      </div>
                    </Col>
                  </Row>

                  <div className="actions" style={{ marginTop: 8 }}>
                    <Button color="primary" type="submit" disabled={saving}>
                      {saving ? "Saving..." : formMode}
                    </Button>
                  </div>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      )}

      {/* === Table === */}
      <Row>
        <Col sm="12">
          <Card
            style={{
              padding: 8,
              borderRadius: 14,
              border: "1px solid #e5e7eb",
            }}
          >
            {noData ? (
              <div
                style={{ padding: 24, textAlign: "center", color: "#6b7280" }}
              >
                No warehouses found.
              </div>
            ) : (
              <div className="wh-table-wrap">
                <table className="wh-table">
                  <thead>
                    <tr>
                      {visibleCols.map((c) => (
                        <th key={c.key}>{c.header}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((r) => (
                      <tr key={r?._id || r?.id}>
                        {visibleCols.map((c) => (
                          <td key={c.key}>
                            {typeof c.render === "function"
                              ? c.render(r)
                              : toStr(c.get(r))}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </Col>
      </Row>

      {/* Column Chooser Modal */}
      <Modal
        isOpen={modal}
        toggle={closeModal}
        zIndex={2000}
        backdrop="static"
        keyboard={false}
        style={{ maxWidth: 1050 }}
      >
        <ModalHeader toggle={closeModal}>Change Fields</ModalHeader>
        <ModalBody>
          <Row>
            <Col lg="4" md="4" sm="12" xl="4" xs="12">
              <h5>Available Columns</h5>
              <div style={listBoxStyle}>
                {baseColumns.map((col) => (
                  <div key={col.key} style={pickItemStyle}>
                    <label
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <input
                        type="checkbox"
                        onChange={(e) =>
                          handleCheckbox(e.target.checked, col.key)
                        }
                      />
                      <span>{col.header}</span>
                    </label>
                  </div>
                ))}
              </div>
              <div style={{ marginTop: 8 }}>
                <Button size="sm" color="primary" onClick={addSelectedColumns}>
                  Add Selected →
                </Button>
              </div>
            </Col>

            <Col lg="8" md="8" sm="12" xl="8" xs="12">
              <h5>Visible Columns</h5>
              <div
                style={{ display: "flex", gap: 12, alignItems: "flex-start" }}
              >
                <div style={{ ...listBoxStyle, flex: 1 }}>
                  {selectedKeys.map((key, i) => {
                    const col = colMap.get(key);
                    if (!col) return null;
                    return (
                      <div
                        key={key}
                        style={{
                          ...pickItemStyle,
                          background: i === selectedIndex ? "#1877f2" : "#fff",
                          color: i === selectedIndex ? "#fff" : "#000",
                          cursor: "pointer",
                        }}
                        onClick={() => setSelectedIndex(i)}
                      >
                        {col.header}
                      </div>
                    );
                  })}
                </div>
                <div style={{ display: "grid", gap: 8 }}>
                  <Button size="sm" onClick={shiftUp}>
                    ↑
                  </Button>
                  <Button size="sm" onClick={shiftDown}>
                    ↓
                  </Button>
                  <Button size="sm" color="danger" onClick={removeAtIndex}>
                    Remove
                  </Button>
                </div>
              </div>
              <div style={{ marginTop: 12, textAlign: "center" }}>
                <Button color="primary" onClick={applyColumns}>
                  Submit
                </Button>
              </div>
            </Col>
          </Row>
        </ModalBody>
      </Modal>
    </>
  );
}

/* little styles */
const dlItemStyle = {
  padding: "10px 12px",
  cursor: "pointer",
  fontWeight: 600,
  letterSpacing: ".02em",
};
const listBoxStyle = {
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  maxHeight: 280,
  overflow: "auto",
  background: "#fff",
  padding: 8,
};
const pickItemStyle = {
  padding: "8px 10px",
  borderRadius: 8,
  border: "1px solid #e5e7eb",
  marginBottom: 6,
  background: "#fff",
};
